import React, { useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import {
  MapOptionType,
  LocationFilterSizeType,
  SelectOptionType,
  LocationLabels,
  MUILocationGridFilterType,
} from '../utils/Types';
import {
  fetchVillagesOfDistricts,
  locationFilterSelector,
} from '../features/locationFilterSlice';
import { useAppDispatch, useAppSelector } from '../store/redux-hooks';
import MSelect from './MSelect';
import {
  DEFAULT_OPTION_LOCATION_SET_TARGET,
  NO_DATA_OPTION,
} from '../utils/Constant';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import { CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material';

interface ITrackLocationFilterProps {
  filterData: MUILocationGridFilterType;
  setLocationFilter: (filterData: MUILocationGridFilterType) => void;
  size?: LocationFilterSizeType;
  disabled?: boolean;
  defaultLocationOptions?: any;
  required?: boolean;
  labels?: LocationLabels;
  dataTestIds?: LocationLabels;
  includeAllOption?: boolean;
  includeCityAllOption?: boolean;
}

const MUILocationGridFilter: React.FunctionComponent<
  ITrackLocationFilterProps
> = ({
  filterData,
  setLocationFilter,
  size,
  disabled = false,
  defaultLocationOptions,
  required = false,
  labels,
  dataTestIds,
  includeAllOption,
  includeCityAllOption = false,
}) => {
  const dispatch = useAppDispatch();
  const [stateOption, setStateOption] = useState<SelectOptionType[]>([
    includeAllOption ? DEFAULT_OPTION_LOCATION_SET_TARGET : NO_DATA_OPTION,
  ]);
  const [districtOption, setDistrictOption] = useState<SelectOptionType[]>([
    includeAllOption ? DEFAULT_OPTION_LOCATION_SET_TARGET : NO_DATA_OPTION,
  ]);
  const districtData: MapOptionType = useAppSelector(
    locationFilterSelector
  ).districtData;
  const cityOptions: MapOptionType = useAppSelector(
    locationFilterSelector
  ).cityOptions;
  const allStates: MapOptionType = useAppSelector(
    locationFilterSelector
  ).statesData;
  const zoneOptionRaw: SelectOptionType[] = useAppSelector(
    locationFilterSelector
  ).zoneOptions;
  const zoneOption =
    includeAllOption && zoneOptionRaw && zoneOptionRaw.length > 1
      ? [DEFAULT_OPTION_LOCATION_SET_TARGET, ...zoneOptionRaw]
      : zoneOptionRaw;

  const setStateArrayData = (array: SelectOptionType[]) => {
    if (array && array.length === 1) {
      setStateOption(array);
    } else if (includeAllOption) {
      setStateOption([DEFAULT_OPTION_LOCATION_SET_TARGET, ...(array || [])]);
    } else {
      setStateOption(array || [NO_DATA_OPTION]);
    }
  };

  const onZoneChange = (event: any) => {
    setLocationFilter({
      ...filterData,
      zone: event.target.value,
      state: includeAllOption ? 'All' : '',
      district: includeAllOption ? 'All' : '',
      city: includeCityAllOption ? [] : [],
    });
    setStateArrayData(
      event?.target?.value !== 'All' ? allStates[event.target.value] : []
    );
    setDistrictOption([
      includeAllOption ? DEFAULT_OPTION_LOCATION_SET_TARGET : NO_DATA_OPTION,
    ]);
  };

  const onStateChange = (event: any) => {
    setLocationFilter({
      ...filterData,
      state: event.target.value,
      district: includeAllOption ? 'All' : '',
      city: includeCityAllOption ? [] : [],
    });
    setDistrictOption(
      event?.target?.value
        ? [
            ...(includeAllOption ? [DEFAULT_OPTION_LOCATION_SET_TARGET] : []),
            ...(districtData[event.target.value] || []),
          ]
        : []
    );
  };

  useEffect(() => {
    if (
      !defaultLocationOptions?.zoneOption?.length &&
      zoneOption &&
      zoneOption.length === 1
    ) {
      onZoneChange({ target: { value: zoneOption[0].value } });
    }
  }, [zoneOption]);

  useEffect(() => {
    if (
      !defaultLocationOptions?.zoneOption?.length &&
      stateOption &&
      stateOption.length === 1
    ) {
      onStateChange({ target: { value: stateOption[0].value } });
    }
  }, [stateOption]);

  useEffect(() => {
    setStateArrayData(
      filterData?.zone && filterData.zone !== 'All'
        ? allStates[filterData.zone]
        : []
    );
  }, [filterData?.zone]);

  const icon = <CheckBoxOutlineBlank fontSize="small" />;
  const checkedIcon = <CheckBox fontSize="small" />;

  const villageOptions =
    defaultLocationOptions?.cityOption ||
    (filterData.district
      ? [
          ...(includeCityAllOption ? [DEFAULT_OPTION_LOCATION_SET_TARGET] : []),
          ...(cityOptions?.[filterData.district] ?? []),
        ]
      : [NO_DATA_OPTION]);

  const allVillages = filterData.district
    ? cityOptions[filterData.district] ?? []
    : [];
  const isAllSelected =
    filterData.city.length > 0 &&
    allVillages.length > 0 &&
    filterData.city.length === allVillages.length;

  return (
    <Row>
      <Col xs={size?.zone?.xs ?? 6} md={size?.zone?.md ?? 2} className="mb-3">
        <MSelect
          label={labels?.zone ?? 'Zone'}
          options={
            defaultLocationOptions?.zoneOption ?? zoneOption ?? [NO_DATA_OPTION]
          }
          value={filterData.zone}
          required={required}
          onChange={onZoneChange}
          disabled={disabled}
          dataTestId={dataTestIds?.zone}
        />
      </Col>
      <Col xs={size?.zone?.xs ?? 6} md={size?.zone?.md ?? 4} className="mb-3">
        <MSelect
          label={labels?.state ?? 'State'}
          required={required}
          options={
            defaultLocationOptions?.stateOption ??
            (filterData.zone
              ? stateOption ?? [NO_DATA_OPTION]
              : [NO_DATA_OPTION])
          }
          value={filterData.state ?? ''}
          onChange={onStateChange}
          disabled={disabled}
          dataTestId={dataTestIds?.state}
        />
      </Col>
      <Col xs={size?.zone?.xs ?? 6} md={size?.zone?.md ?? 2} className="mb-2">
        <MSelect
          label={labels?.district ?? 'District'}
          required={required}
          options={
            defaultLocationOptions?.districtOption ??
            (filterData.state
              ? districtOption ?? [NO_DATA_OPTION]
              : [NO_DATA_OPTION])
          }
          value={filterData.district ?? ''}
          onChange={(event: any) => {
            setLocationFilter({
              ...filterData,
              district: event.target.value,
              city: includeCityAllOption ? [] : [],
            });

            if (event?.target?.value !== 'All') {
              if (!cityOptions[event.target.value]) {
                dispatch(
                  fetchVillagesOfDistricts({
                    districtId: [Number(event.target.value)],
                  })
                );
              }
            }
          }}
          disabled={disabled}
          dataTestId={dataTestIds?.district}
        />
      </Col>
      <Col xs={size?.zone?.xs ?? 6} md={size?.zone?.md ?? 4} className="mb-2">
        <Autocomplete
          multiple
          disableCloseOnSelect
          disableClearable
          clearIcon={null}
          noOptionsText="No village found"
          options={villageOptions}
          getOptionLabel={(option) => option.label}
          filterOptions={(options, { inputValue }) => {
            const normalizedInput = inputValue.toLowerCase();
            return options.filter(
              (opt: any) =>
                opt.label.toLowerCase().includes(normalizedInput) ||
                opt.value.toLowerCase().includes(normalizedInput)
            );
          }}
          value={
            filterData.city.includes('All')
              ? [DEFAULT_OPTION_LOCATION_SET_TARGET] // only "All" visibly selected
              : villageOptions.filter((opt: any) =>
                  filterData.city.includes(opt.value)
                )
          }
          onChange={(event, selectedOptions) => {
            const selectedValues = selectedOptions.map((opt) =>
              String(opt.value)
            );
            const isAllClicked = selectedValues.includes('All');
            const allVillages = filterData.district
              ? cityOptions[filterData.district] ?? []
              : [];

            let finalValues: string[] = [];
            let finalPincodes: string[] = [];

            if (isAllClicked) {
              const wasAllSelected =
                filterData.city.length > 0 &&
                allVillages.length > 0 &&
                filterData.city.length === allVillages.length;

              if (!wasAllSelected) {
                // Selecting "All" → select all real villages
                finalValues = allVillages.map((opt) => String(opt.value));
                finalPincodes = allVillages.map((opt) => String(opt.pincode));
              } else {
                // Deselecting "All" → clear all
                finalValues = [];
                finalPincodes = [];
              }
            } else {
              // Selecting manually
              finalValues = selectedValues.filter((v) => v !== 'All'); // exclude "All"
              finalPincodes = selectedOptions
                .filter((opt) => opt.value !== 'All')
                .map((opt) => String(opt.pincode));
            }

            setLocationFilter({
              ...filterData,
              city: finalValues,
              pincode: finalPincodes,
            });
          }}
          renderOption={(props, option, { selected }) => {
            const isOptionSelected =
              option.value === 'All' ? isAllSelected : selected;

            return (
              <li {...props} key={`${option.value}-${option.label}`}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={isOptionSelected}
                />
                {option.label}
              </li>
            );
          }}
          renderTags={() => null}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              fullWidth
              required={required}
              label={labels?.city ?? 'Village'}
              InputLabelProps={{
                sx: {
                  fontSize: '12px',
                  pb: '4px',
                  '&.MuiInputLabel-shrink': {
                    fontSize: '14px',
                    color: 'black',
                  },
                  '& .MuiFormLabel-asterisk': {
                    color: 'red',
                  },
                },
              }}
              placeholder="Search villages"
              inputProps={{
                ...params.inputProps,
                readOnly: isAllSelected,
                style: {
                  fontSize: '12px',
                },
              }}
              InputProps={{
                ...params.InputProps,
                disableUnderline: false,
                style: {
                  fontSize: '12px',
                },
              }}
            />
          )}
          disabled={disabled}
          data-testid={dataTestIds?.city}
        />
      </Col>
    </Row>
  );
};

export default MUILocationGridFilter;
